import { ApolloQueryResult, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import React, {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  IBreadcrumb,
  IParamPick,
  IIGData,
  INavItem,
  IPlayer,
  IProductDetail,
  IProductSlide,
  IReview,
  IStories,
} from '../components/types'
import {
  createBreadcrumbs,
  createProduct,
  createProductInstagram,
  createProductSlider,
  createStories,
  getSelectedSku,
} from '../lib/createProduct'
import { createReview } from '../lib/createReview'
import { createVideo } from '../lib/createVideo'
import { getElementVariables, getSubSectionsVariables } from '../lib/gql'
import { PRODUCT, PRODUCT_DISCOUNT, SECTIONS } from '../queries/catalogQuery'
import { REVIEWS } from '../queries/reviewQuery'
import {
  product,
  productVariables,
  product_product_sku_propertyValuesRelation_targetElement,
} from '../__generated__/product'
import { reviews, reviewsVariables } from '../__generated__/reviews'
import { video_fields } from '../__generated__/video_fields'
import {
  productDiscount,
  productDiscountVariables,
} from '../__generated__/productDiscount'
import { sections, sectionsVariables } from '../__generated__/sections'
import { CATALOG_HREF } from '../components/data'
import { LayoutContext } from './layoutProvider'
import { SortOrder } from '../__generated__/globalTypes'

interface IProductContext {
  product: IProductDetail
  stories?: IStories[]
  reviews?: IReview[]
  breadcrumbs: IBreadcrumb[]
  slides: IProductSlide[]
  pickedSku: IParamPick
  setPickedSku: Dispatch<SetStateAction<IParamPick>>
  pickedSkuAroma: IParamPick
  setPickedSkuAroma: Dispatch<SetStateAction<IParamPick>>
  test: product_product_sku_propertyValuesRelation_targetElement
  video: IPlayer
  loadingReviews: boolean
  pickedColors: Record<string, IParamPick>
  pickColor: (setId: string, sku: IParamPick) => void
  hasSubsets: boolean
  subsetsIds: string[]
  discountActive: boolean
  discountActiveTo?: number
  refetch: (
    variables?: Partial<productVariables>,
  ) => Promise<ApolloQueryResult<product>>
  instagram: IIGData[]
}
export const ProductDetailContext = createContext({} as IProductContext)

const ProductDetailProvider: FC<{
  elementCode: string
  sectionCode: string
  parentSectionCode: string
  today: string
}> = ({ children, elementCode, sectionCode, parentSectionCode, today }) => {
  const init = useRef(false)
  const { setInnerNav } = useContext(LayoutContext)

  const router = useRouter()
  const { query, pathname } = router
  const { data, refetch } = useQuery<product, productVariables>(PRODUCT, {
    variables: getElementVariables({ elementCode, sectionCode, today }),
    fetchPolicy: 'cache-only',
  })

  // get subsections list
  const { data: dataSubSections } = useQuery<sections, sectionsVariables>(
    SECTIONS,
    {
      variables: getSubSectionsVariables({ sectionCode: parentSectionCode }),
      fetchPolicy: 'cache-only',
    },
  )

  const makeInnerNav = () => {
    const innerNav: INavItem[] = []

    innerNav.push({
      href: CATALOG_HREF,
      as: `/catalog/${parentSectionCode}`,
      selected: !sectionCode,
      title: 'Все',
      code: parentSectionCode,
    })
    dataSubSections?.sections?.nodes?.map((section) => {
      innerNav.push({
        href: CATALOG_HREF,
        as: `/catalog/${parentSectionCode}/${section?.code}`,
        selected: sectionCode && sectionCode === section?.code,
        title: section.name,
        code: `inner-nav-${section?.code}`,
      })
    })

    setInnerNav(innerNav)
  }

  useEffect(() => {
    if (dataSubSections) {
      makeInnerNav()
    }
  }, [sectionCode, dataSubSections])

  // const { data: discountData } = useQuery<
  //   productDiscount,
  //   productDiscountVariables
  // >(PRODUCT_DISCOUNT, {
  //   variables: {
  //     productId: data?.product?.id,
  //   },
  //   skip: !data?.product?.id,
  // })
  // const instagram = createProductInstagram(data?.instagram?.nodes)

  // const discountFrom =
  //   discountData?.productDiscount?.activeFrom &&
  //   new Date(discountData?.productDiscount?.activeFrom).getTime()

  // const now = new Date().getTime()
  // const discountActive = discountFrom && now && discountFrom < now

  // const discountActiveTo =
  //   discountData?.productDiscount?.activeTo &&
  //   new Date(discountData?.productDiscount?.activeTo).getTime()

  const reviewVariables: reviewsVariables = {
    where: {
      iblock: { code: { equals: 'form-reviews' } },
      active: { equals: true },
      propertyValuesRelation: {
        some: { targetElement: { id: { equals: data?.product?.id } } },
      },
    },
    order: {
      createdAt: SortOrder.desc,
    },
    first: 999,
  }
  const { data: dataReviews, loading: loadingReviews } = useQuery<
    reviews,
    reviewsVariables
  >(REVIEWS, {
    variables: reviewVariables,
  })

  const product = createProduct(data?.product)

  const [pickedColors, setPickedColors] = useState<Record<string, IParamPick>>(
    product?.subsets?.length ? getDefaultColors(product?.subsets) : {},
  )

  function pickColor(setId: string, sku: IParamPick) {
    setPickedColors({
      ...pickedColors,
      [setId]: sku,
    })
  }

  const [pickedSku, setPickedSku] = useState<IParamPick>(null)
  const [pickedSkuAroma, setPickedSkuAroma] = useState<IParamPick>(null)

  useEffect(() => {
    return () => {
      setPickedSku(null)
      setPickedSkuAroma(null)
    }
  }, [elementCode])

  const { data: discountData } = useQuery<
    productDiscount,
    productDiscountVariables
  >(PRODUCT_DISCOUNT, {
    variables: {
      productId: pickedSku ? pickedSku?.id : data?.product?.id,
    },
    skip: !pickedSku?.id && !data?.product?.id,
  })

  const instagram = createProductInstagram(data?.instagram?.nodes)

  const discountFrom =
    discountData?.productDiscount?.activeFrom &&
    new Date(discountData?.productDiscount?.activeFrom).getTime()

  const now = new Date().getTime()
  const discountActive = discountFrom && now && discountFrom < now

  const discountActiveTo =
    discountData?.productDiscount?.activeTo &&
    new Date(discountData?.productDiscount?.activeTo).getTime()

  const breadcrumbs = createBreadcrumbs(data?.product)
  const stories = createStories(data?.product)
  const reviews = dataReviews?.elements?.edges.map((edge) =>
    createReview(edge.node),
  )

  useEffect(() => {
    if (!pickedSku && product?.colors?.length > 0 && !query.offer) {
      setPickedSku(product.colors[0])
    }

    if (!pickedSkuAroma && product?.aromates?.length > 0 && !query.offer) {
      setPickedSkuAroma(product.aromates[0])
    }

    if (!Object.keys(pickedColors).length && product?.subsets?.length) {
      setPickedColors(getDefaultColors(product?.subsets))
    }
  }, [product?.colors, product?.aromates])

  useEffect(() => {
    if (pickedSku && +query?.offer !== pickedSku.bx_id) {
      router.replace(
        {
          pathname,
          query: { ...query, offer: pickedSku?.bx_id },
        },
        null,
        {
          scroll: false,
          shallow: true,
        },
      )
    }

    if (pickedSkuAroma && +query?.offer !== pickedSkuAroma.bx_id) {
      router.replace(
        {
          pathname,
          query: { ...query, offer: pickedSkuAroma?.bx_id },
        },
        null,
        {
          scroll: false,
          shallow: true,
        },
      )
    }
  }, [pickedSku, pickedSkuAroma])

  useEffect(() => {
    if (query.offer && product?.colors?.length > 0) {
      const offer = product.colors.filter(
        (color) => color?.bx_id === +query.offer,
      )[0]
      if (offer && +query.offer !== pickedSku?.bx_id) {
        setPickedSku(offer)
      }
    }

    if (query.offer && product?.aromates?.length > 0) {
      const offer = product.aromates.filter(
        (aroma) => aroma?.bx_id === +query.offer,
      )[0]
      if (offer && +query.offer !== pickedSkuAroma?.bx_id) {
        setPickedSkuAroma(offer)
      }
    }
  }, [query, product?.colors, product?.aromates])

  const slides = createProductSlider(
    data?.product,
    pickedSku?.id || pickedSkuAroma?.id,
  )
  const test = getSelectedSku(data?.product, pickedSku?.id)
  const video = createVideo(
    data?.product?.propertyValuesRelation?.find(
      (val) => val?.property?.code === 'VIDEO_ID',
    ) as unknown as video_fields,
  )

  const hasSubsets = product?.subsets?.length > 0

  const subsetsIds = Object.keys(pickedColors)?.map?.(
    (key) => pickedColors[key].id,
  )

  useEffect(() => {
    init.current = false
  }, [elementCode])

  useEffect(() => {
    if (!init.current && window) {
      const w = window as any
      let name = product?.subtitle
        ? `${data?.product?.name} ${product.subtitle}`
        : data?.product?.name
      let id = product?.bx_id

      if (pickedSku) {
        name += ` ${pickedSku.artNumber && pickedSku.artNumber} ${
          pickedSku?.item
        }`
        id = pickedSku.bx_id
      }

      if (pickedSkuAroma) {
        name += ` ${pickedSkuAroma.artNumber && pickedSkuAroma.artNumber} ${
          pickedSkuAroma?.item
        }`
        id = pickedSkuAroma.bx_id
      }

      w?.dataLayer?.push({
        ecommerce: {
          currencyCode: 'RUB',
          detail: {
            products: [
              {
                id,
                name,
              },
            ],
          },
        },
      })
      init.current = true
    }
  }, [init.current])

  return (
    <ProductDetailContext.Provider
      value={{
        product,
        stories,
        reviews,
        loadingReviews,
        breadcrumbs,
        slides,
        pickedSku,
        pickedSkuAroma,
        setPickedSku,
        setPickedSkuAroma,
        test,
        video,
        pickedColors,
        pickColor,
        hasSubsets,
        subsetsIds,
        discountActive,
        discountActiveTo,
        refetch,
        instagram,
      }}
    >
      {children}
    </ProductDetailContext.Provider>
  )
}

function getDefaultColors(
  subsets: IProductDetail[],
): Record<string, IParamPick> {
  const result: Record<string, IParamPick> = {}
  subsets?.forEach((subset) => {
    const firstColor = subset.colors[0]
    if (firstColor) {
      result[subset.id] = firstColor
    }
  })
  return result
}

export default ProductDetailProvider
